import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { persistQueryCache } from "@/lib/query-persist";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { useEffect, useState, lazy, Suspense, type ReactNode } from "react";

import appCss from "../styles.css?url";

import { reportLovableError } from "../lib/lovable-error-reporting";
import { LocaleProvider } from "@/lib/i18n";
// Perf: o aviso de cookies puxa Dialog + Switch. Fica fora do pacote inicial e
// só monta depois que a página já pintou (idle ou primeira interação).
const CookieConsent = lazy(() =>
  import("@/components/CookieConsent").then((m) => ({ default: m.CookieConsent })),
);
// Toaster (sonner) is only needed once something calls toast() — keep it out of
// the initial entry chunk so every page (incl. 404) boots with less JS.
const Toaster = lazy(() =>
  import("@/components/ui/sonner").then((m) => ({ default: m.Toaster })),
);





function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: unknown; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "Viny Club" },
      {
        name: "description",
        content:
          "Viny Club — comunidade dos colecionadores de vinil. Catalogue sua coleção de discos de vinil, descubra prensagens raras e troque LPs com outros colecionadores.",
      },
      { name: "author", content: "Viny Club" },
      { name: "application-name", content: "Viny Club" },
      { name: "apple-mobile-web-app-capable", content: "yes" },
      { name: "apple-mobile-web-app-status-bar-style", content: "black-translucent" },
      { name: "apple-mobile-web-app-title", content: "Viny Club" },
      { name: "theme-color", content: "#0B0B0B" },
      { property: "og:title", content: "Viny Club" },
      {
        property: "og:description",
        content:
          "Comunidade dos colecionadores de vinil: catalogue seus discos de vinil, monte o mini site da sua coleção e troque com outros colecionadores.",
      },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "Viny Club" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Viny Club" },
      {
        name: "twitter:description",
        content:
          "Comunidade dos colecionadores de vinil: catalogue, exiba e troque seus discos de vinil com a comunidade.",
      },
      { property: "og:image", content: "https://viny.club/og-image.jpg" },
      { name: "twitter:image", content: "https://viny.club/og-image.jpg" },
      { name: "google-site-verification", content: "wajxhYNDnkXmp0w0v2236ZNwvNNZIH2dv8bcTXIRbF4" },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "icon", type: "image/png", href: "/favicon.png" },
      { rel: "apple-touch-icon", sizes: "180x180", href: "/apple-touch-icon.png" },
      { rel: "manifest", href: "/manifest.webmanifest" },
      // Perf: no máximo 3 preconnects (fontes, capas, backend). O resto é só DNS,
      // porque preconnect em excesso rouba banda do LCP no mobile.
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
      { rel: "preconnect", href: "https://wsrv.nl", crossOrigin: "anonymous" },
      { rel: "preconnect", href: "https://i.scdn.co", crossOrigin: "anonymous" },
      { rel: "dns-prefetch", href: "https://zczsvrasrirhkimclbwi.supabase.co" },
      { rel: "dns-prefetch", href: "https://fonts.googleapis.com" },
      // Capas vindas do Discogs e do Deezer (fallbacks quando o Spotify não tem).
      { rel: "dns-prefetch", href: "https://i.discogs.com" },
      { rel: "dns-prefetch", href: "https://cdn-images.dzcdn.net" },
      { rel: "dns-prefetch", href: "https://mosaic.scdn.co" },
      { rel: "dns-prefetch", href: "https://storage.googleapis.com" },
      // Senior perf: load Google Fonts non-blocking via media="print" swap trick.
      // The inline script below flips media back to "all" after the stylesheet
      // loads so the browser can paint the hero <h1> without waiting on fonts
      // (saves ~700ms render-blocking on mobile).
      {
        rel: "preload",
        as: "style",
        href: "https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=Space+Grotesk:wght@500;600;700&family=Instrument+Serif:ital@0;1&display=swap",
      },
      {
        rel: "stylesheet",
        href: "https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=Space+Grotesk:wght@500;600;700&family=Instrument+Serif:ital@0;1&display=swap",
        media: "print",
        "data-viny-font": "swap",
      } as unknown as { rel: string; href: string; media: string },
    ],
    scripts: [
      // Flip Google Fonts stylesheet from media="print" to "all" once ready,
      // so it stops render-blocking the initial paint.
      { type: "text/javascript", children: "(function(){var l=document.querySelector('link[data-viny-font=\"swap\"]');if(!l)return;var apply=function(){l.media='all'};if(l.sheet){apply()}else{l.addEventListener('load',apply,{once:true});setTimeout(apply,3000)}})();" },
      // Rede de segurança para capas: qualquer <img> que falhar (host fora do
      // ar, Cover Art Archive 404, URL antiga) cai no disco cinza em vez de
      // ficar com a imagem quebrada. Um único listener cobre todas as telas.
      {
        type: "text/javascript",
        children:
          "(function(){var F='data:image/svg+xml;charset=utf-8,'+encodeURIComponent('<svg xmlns=\\'http://www.w3.org/2000/svg\\' width=\\'160\\' height=\\'160\\'><rect width=\\'160\\' height=\\'160\\' fill=\\'#efefef\\'/><circle cx=\\'80\\' cy=\\'80\\' r=\\'46\\' fill=\\'#d9d9d9\\'/><circle cx=\\'80\\' cy=\\'80\\' r=\\'10\\' fill=\\'#efefef\\'/></svg>');document.addEventListener('error',function(e){var t=e.target;if(!t||t.tagName!=='IMG')return;if(t.dataset.coverFallback==='1')return;t.dataset.coverFallback='1';t.src=F;},true);})();",
      },
      // Defer GA until the browser is idle so it never blocks LCP/TTI.
      { type: "text/javascript", children: "window.dataLayer = window.dataLayer || []; function gtag(){dataLayer.push(arguments);} var __vinyGaDone=false; var __vinyGaBoot=function(){if(__vinyGaDone)return;__vinyGaDone=true;var s=document.createElement('script');s.async=true;s.src='https://www.googletagmanager.com/gtag/js?id=G-H4XHNGRQQB';document.head.appendChild(s);gtag('js', new Date()); gtag('config', 'G-H4XHNGRQQB');}; ['pointerdown','keydown','scroll','touchstart'].forEach(function(e){window.addEventListener(e,__vinyGaBoot,{once:true,passive:true})}); setTimeout(function(){if('requestIdleCallback' in window){requestIdleCallback(__vinyGaBoot,{timeout:4000})}else{__vinyGaBoot()}},8000);" },





      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          name: "Viny Club",
          url: "https://viny.club",
          description:
            "Comunidade dos colecionadores de vinil no Brasil. Catálogo de discos de vinil, link na bio e troca entre colecionadores.",
          inLanguage: "pt-BR",
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "Viny Club",
          url: "https://viny.club",
          description:
            "Plataforma interativa e comunidade dos colecionadores de vinil — não é loja virtual de discos.",
        }),
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="pt-BR">
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

/** Só carrega o aviso de cookies quando o navegador estiver livre. */
function useDeferred(delay = 1500) {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const start = () => setReady(true);
    const idle = (window as any).requestIdleCallback as
      | ((cb: () => void, o?: { timeout: number }) => number)
      | undefined;
    const id = idle ? idle(start, { timeout: delay }) : window.setTimeout(start, delay);
    return () => {
      if (idle && (window as any).cancelIdleCallback) (window as any).cancelIdleCallback(id);
      else window.clearTimeout(id);
    };
  }, [delay]);
  return ready;
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  const consentReady = useDeferred();

  // Perf: feed e listas públicas reaparecem na hora ao voltar ao app.
  useEffect(() => persistQueryCache(queryClient), [queryClient]);

  return (
    <QueryClientProvider client={queryClient}>
      <LocaleProvider>
      <Outlet />
      <Suspense fallback={null}>
        <Toaster />
      </Suspense>
      {consentReady && (
        <Suspense fallback={null}>
          <CookieConsent />
        </Suspense>
      )}
      </LocaleProvider>

    </QueryClientProvider>

  );
}

